import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../../lib/api-client';
import { StatCard } from '../../components/ui/stat-card';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { getMonthOptions, currentMonth, getDateRangeForMonth, formatNum } from '../../lib/month';
import { useMdr } from '../../hooks/useMdr';

interface DashboardResponse {
  month: string;
  merchantCount: number;
  payinVolume: number;
  payoutVolume: number;
  payinFee: number;
  payoutFee: number;
}

interface MerchantItem {
  merchantId: string;
  code: string;
  name: string;
  status: 'active' | 'inactive' | 'locked';
  lastStatSyncAt: string | null;
  masterPayinFeePercent: number;
  masterPayoutFeePercent: number;
  payinFeePercent?: number;
  payoutFeePercent?: number;
}

interface StatRow {
  deposit: number;
  topup: number;
  withdraw: number;
  settle: number;
}

interface MerchantTotals {
  deposit: number;
  topup: number;
  withdraw: number;
  settle: number;
}

// Freshness cutoff: a sync at-or-after this Date covers the period fully.
// Past month → start of next month (GMT+7); current month → start of today.
function syncCutoff(yearMonth: string): Date {
  const [year, month] = yearMonth.split('-').map(Number);
  const now = new Date();
  const isCurrent = year === now.getFullYear() && month === now.getMonth() + 1;
  const offset = 7 * 60 * 60 * 1000;
  if (isCurrent) {
    const shifted = new Date(now.getTime() + offset);
    const startOfDayUtc = Date.UTC(
      shifted.getUTCFullYear(),
      shifted.getUTCMonth(),
      shifted.getUTCDate(),
    );
    return new Date(startOfDayUtc - offset);
  }
  return new Date(`${year}-${String(month + 1).padStart(2, '0')}-01T00:00:00.000+07:00`);
}

export function MdrDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isMaster } = useMdr();
  const [month, setMonth] = useState(currentMonth());
  const monthOptions = getMonthOptions();
  const { dateFrom, dateTo } = getDateRangeForMonth(month);
  const syncDate = new Date(`${month}-01T00:00:00.000+07:00`).toISOString();

  const { data, isLoading } = useQuery({
    queryKey: ['mdr', 'dashboard', month],
    queryFn: () => apiGet<DashboardResponse>('/mdr/dashboard', { month }),
  });

  const { data: merchantsData } = useQuery({
    queryKey: ['mdr', 'merchants'],
    queryFn: () => apiGet<{ items: MerchantItem[]; total: number }>('/mdr/merchants'),
  });

  const merchants = merchantsData?.ok ? merchantsData.data.items : [];
  const merchantIds = merchants.map((m) => m.merchantId);

  // Fetch every merchant's month stats together so we can total them and so a
  // per-row sync / sync-all can refresh in one place.
  const { data: statsMap, isFetching: statsFetching } = useQuery({
    queryKey: ['mdr', 'dash-stats', month, merchantIds.join(',')],
    enabled: merchants.length > 0,
    queryFn: async () => {
      const entries = await Promise.all(
        merchants.map(async (m): Promise<[string, MerchantTotals]> => {
          const res = await apiGet<{ items: StatRow[] }>(`/mdr/merchants/${m.merchantId}/stats`, {
            dateFrom,
            dateTo,
            granularity: 'daily',
            pageSize: '100',
          });
          const items = res.ok ? res.data.items : [];
          return [
            m.merchantId,
            {
              deposit: items.reduce((s, r) => s + r.deposit, 0),
              topup: items.reduce((s, r) => s + r.topup, 0),
              withdraw: items.reduce((s, r) => s + r.withdraw, 0),
              settle: items.reduce((s, r) => s + r.settle, 0),
            },
          ];
        }),
      );
      return Object.fromEntries(entries) as Record<string, MerchantTotals>;
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['mdr', 'dash-stats'] });
    queryClient.invalidateQueries({ queryKey: ['mdr', 'merchants'] });
    queryClient.invalidateQueries({ queryKey: ['mdr', 'dashboard'] });
  };

  const syncOne = useMutation({
    mutationFn: (merchantId: string) =>
      apiPost(`/mdr/merchants/${merchantId}/sync`, { queryParams: { date: syncDate } }),
    onSuccess: (res) => {
      if (res.ok) invalidate();
    },
  });

  const syncAll = useMutation({
    mutationFn: async () => {
      await Promise.all(
        merchants.map((m) =>
          apiPost(`/mdr/merchants/${m.merchantId}/sync`, { queryParams: { date: syncDate } }),
        ),
      );
    },
    onSuccess: () => invalidate(),
  });

  const d = data?.ok ? data.data : null;
  const feeLabel = isMaster ? t('mdr.masterFee') : t('mdr.myShare');

  const feePct = (m: MerchantItem) => ({
    payin: isMaster ? m.masterPayinFeePercent : (m.payinFeePercent ?? 0),
    payout: isMaster ? m.masterPayoutFeePercent : (m.payoutFeePercent ?? 0),
  });

  // Build rows + totals from the centralised stats map.
  const rows = merchants.map((m) => {
    const s = statsMap?.[m.merchantId] ?? { deposit: 0, topup: 0, withdraw: 0, settle: 0 };
    const pct = feePct(m);
    const payinFee = ((s.deposit + s.topup) * pct.payin) / 100;
    const payoutFee = ((s.withdraw + s.settle) * pct.payout) / 100;
    return { m, s, pct, payinFee, payoutFee };
  });
  const totals = rows.reduce(
    (acc, r) => ({
      deposit: acc.deposit + r.s.deposit,
      topup: acc.topup + r.s.topup,
      withdraw: acc.withdraw + r.s.withdraw,
      settle: acc.settle + r.s.settle,
      payinFee: acc.payinFee + r.payinFee,
      payoutFee: acc.payoutFee + r.payoutFee,
    }),
    { deposit: 0, topup: 0, withdraw: 0, settle: 0, payinFee: 0, payoutFee: 0 },
  );

  // Overall sync freshness across all assigned merchants.
  const cutoff = syncCutoff(month);
  const syncDates = merchants.map((m) => (m.lastStatSyncAt ? new Date(m.lastStatSyncAt) : null));
  const anyNever = merchants.length > 0 && syncDates.some((dt) => !dt);
  const anyPartial = syncDates.some((dt) => dt && dt < cutoff);
  const syncStatus: 'none' | 'never' | 'partial' | 'fresh' =
    merchants.length === 0 ? 'none' : anyNever ? 'never' : anyPartial ? 'partial' : 'fresh';
  const oldestSync = syncDates
    .filter((dt): dt is Date => dt !== null)
    .sort((a, b) => a.getTime() - b.getTime())[0];
  const oldestLabel = oldestSync
    ? oldestSync.toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  const fmtLastSync = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' }) : t('admin.neverSynced');

  const show = (v: string) => (statsFetching ? '…' : v);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          {t('mdr.dashboardTitle')}
        </h1>
        <div className="flex items-center gap-3">
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
          >
            {monthOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {isMaster && merchants.length > 0 && (
            <Button size="sm" onClick={() => syncAll.mutate()} disabled={syncAll.isPending}>
              {syncAll.isPending ? t('common.loading') : t('mdr.syncAll')}
            </Button>
          )}
        </div>
      </div>

      {/* Sync freshness banner */}
      {syncStatus === 'never' && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          <div className="font-medium">{t('dashboard.syncNever')}</div>
          <div className="mt-1 text-xs">{t('dashboard.syncNeverHint')}</div>
        </div>
      )}
      {syncStatus === 'partial' && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <div className="font-medium">{t('dashboard.syncPartial')}</div>
          <div className="mt-1 text-xs">
            {oldestLabel && `${t('dashboard.lastSyncAt', { time: oldestLabel })} — `}
            {t('dashboard.syncPartialHint')}
          </div>
        </div>
      )}
      {syncStatus === 'fresh' && oldestLabel && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-xs text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300">
          {t('dashboard.syncFresh', { time: oldestLabel })}
        </div>
      )}

      {isLoading && <div className="text-gray-400">{t('common.loading')}</div>}

      {d && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              label={t('mdr.merchantCount')}
              value={String(d.merchantCount)}
              icon="M"
              accentClass="bg-gradient-primary"
            />
            <StatCard
              label={t('mdr.payinVolume')}
              value={formatNum(d.payinVolume)}
              icon="↘"
              accentClass="bg-gradient-to-tr from-emerald-600 to-emerald-400"
            />
            <StatCard
              label={t('mdr.payoutVolume')}
              value={formatNum(d.payoutVolume)}
              icon="↗"
              accentClass="bg-gradient-to-tr from-orange-600 to-amber-400"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <StatCard
              label={`${feeLabel} — ${t('transactions.calcPayinFee')}`}
              value={formatNum(d.payinFee)}
              icon="∑"
              accentClass="bg-gradient-primary"
            />
            <StatCard
              label={`${feeLabel} — ${t('transactions.calcPayoutFee')}`}
              value={formatNum(d.payoutFee)}
              icon="∑"
              accentClass="bg-gradient-to-tr from-violet-600 to-fuchsia-500"
            />
          </div>
        </>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>{t('mdr.merchantsOverview')}</CardTitle>
            <span className="text-xs font-medium text-gray-400">
              {dateFrom} ~ {dateTo}
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {merchants.length === 0 ? (
            <div className="p-6 text-gray-400">{t('common.noData')}</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('merchants.name')}</TableHead>
                    <TableHead className="text-right">{t('transactions.deposit')}</TableHead>
                    <TableHead className="text-right">{t('transactions.topup')}</TableHead>
                    <TableHead className="text-right">{t('transactions.withdraw')}</TableHead>
                    <TableHead className="text-right">{t('transactions.settle')}</TableHead>
                    <TableHead className="text-right">{t('transactions.calcPayinFee')}</TableHead>
                    <TableHead className="text-right">{t('transactions.calcPayoutFee')}</TableHead>
                    <TableHead className="text-right">{t('dashboard.totalFees')}</TableHead>
                    <TableHead>{t('mdr.lastSync')}</TableHead>
                    {isMaster && (
                      <TableHead className="text-right">{t('common.actions')}</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(({ m, s, pct, payinFee, payoutFee }) => (
                    <TableRow key={m.merchantId}>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => navigate(`/mdr/merchants/${m.merchantId}`)}
                          className="text-left font-medium text-gray-900 hover:text-primary dark:text-gray-100"
                        >
                          {m.name}
                          <span className="ml-1.5 font-mono text-xs text-gray-400">{m.code}</span>
                        </button>
                      </TableCell>
                      <TableCell className="text-right">{show(formatNum(s.deposit))}</TableCell>
                      <TableCell className="text-right">{show(formatNum(s.topup))}</TableCell>
                      <TableCell className="text-right">{show(formatNum(s.withdraw))}</TableCell>
                      <TableCell className="text-right">{show(formatNum(s.settle))}</TableCell>
                      <TableCell className="text-right text-primary">
                        {show(formatNum(payinFee))}
                        <span className="ml-1 text-[0.65rem] text-gray-400">{pct.payin}%</span>
                      </TableCell>
                      <TableCell className="text-right text-emerald-600 dark:text-emerald-400">
                        {show(formatNum(payoutFee))}
                        <span className="ml-1 text-[0.65rem] text-gray-400">{pct.payout}%</span>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-900 dark:text-gray-100">
                        {show(formatNum(payinFee + payoutFee))}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                        {fmtLastSync(m.lastStatSyncAt)}
                      </TableCell>
                      {isMaster && (
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => syncOne.mutate(m.merchantId)}
                            disabled={syncOne.isPending && syncOne.variables === m.merchantId}
                          >
                            {syncOne.isPending && syncOne.variables === m.merchantId
                              ? t('common.loading')
                              : t('admin.syncNow')}
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
                {rows.length > 0 && (
                  <tfoot className="border-t-2 border-gray-200 font-semibold dark:border-gray-600">
                    <TableRow>
                      <TableCell className="text-gray-900 dark:text-gray-100">
                        {t('transactions.total')}
                      </TableCell>
                      <TableCell className="text-right">{show(formatNum(totals.deposit))}</TableCell>
                      <TableCell className="text-right">{show(formatNum(totals.topup))}</TableCell>
                      <TableCell className="text-right">{show(formatNum(totals.withdraw))}</TableCell>
                      <TableCell className="text-right">{show(formatNum(totals.settle))}</TableCell>
                      <TableCell className="text-right text-primary">
                        {show(formatNum(totals.payinFee))}
                      </TableCell>
                      <TableCell className="text-right text-emerald-600 dark:text-emerald-400">
                        {show(formatNum(totals.payoutFee))}
                      </TableCell>
                      <TableCell className="text-right text-gray-900 dark:text-gray-100">
                        {show(formatNum(totals.payinFee + totals.payoutFee))}
                      </TableCell>
                      <TableCell>{null}</TableCell>
                      {isMaster && <TableCell>{null}</TableCell>}
                    </TableRow>
                  </tfoot>
                )}
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
